import React, { useEffect, useState } from "react";
import axios from "axios";
import { CircularProgress, Grid, TextField, Typography } from "@mui/material";
import useTranslation from "../../hooks/translations";
import ButtonStyled from "../ButtonStyled";
import palette from "../../styles/palette";
import { getContrastTextColor } from "../../utils/colorManager";
import { appConfig } from "../../config";
import { Height } from "@mui/icons-material";

const hostedFieldStyle = {
  border: "1px solid #c4c4c4",
  borderRadius: 4,
  padding: "8.5px 14px",
  height: 40,
  boxSizing: "border-box" as const,
};

interface Props {
  onPay: (token: string) => void;
  disabled?: boolean;
  theme?: any;
  amount?: number | null;
  currency?: string | null;
}

const CyberSourceMicroform: React.FC<Props> = ({
  onPay,
  disabled,
  theme,
  amount,
  currency,
}) => {
  const { t } = useTranslation();

  const [captureContext, setCaptureContext] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [microform, setMicroform] = useState<any>(null);
  const [fields, setFields] = useState<{
    number: any;
    securityCode: any;
  } | null>(null);
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Step 1: fetch the capture context JWT from the backend.
  useEffect(() => {
    let active = true;
    axios.defaults.withCredentials = true;
    axios({
      method: "POST",
      baseURL: appConfig.REACT_APP_PROXY_BASE_URL,
      url: "/payment/api/order/cybersource/token",
      withCredentials: true,
    })
      .then((resp) => {
        if (active) setCaptureContext(resp.data.contextToken);
      })
      .catch(() => {
        console.log("HELP_");
        if (active) {
          // setCaptureContext(
          //   "eyJraWQiOiJ6dSIsImFsZyI6IlJTMjU2In0.eyJmbHgiOnsicGF0aCI6Ii9mbGV4L3YyL3Rva2VucyIsInRyYW5zaWVudFRva2VuUmVzcG9uc2VPcHRpb25zIjp7ImluY2x1ZGVDYXJkUHJlZml4IjpmYWxzZX0sImRhdGEiOiJ0RzdlNnhGaTlod0dtQmgyMmdxQXJlT3pkTjl2Uk0tbkJpemhjV0hLdFlYNEwwWm04Zjc5Rl9NOTVpaDRUVXlOWG5SWDlyYkY4Q2YtakpER2N6QlpGa3FtQksxaXhzY0JUd1VwWEljUWRHZHlDdUVndFVCdi13bXY3THJIUnhicFRzckVYemVabWx0QTN2T3ZMd3o3enhmT2xtR0h4TjRtaDljYkZSRHU4UjhqYnRONjhXZnlSSlFHMDBsYVBfUDhSVnFaX3c2ZnQ4R01TY0JtX2pZWmFibTdSTFZON0lnXHUwMDNkIiwib3JpZ2luIjoiaHR0cHM6Ly90ZXN0ZmxleC5jeWJlcnNvdXJjZS5jb20iLCJ0b2tlbnNVcmwiOiJodHRwczovL3Rlc3RmbGV4LmN5YmVyc291cmNlLmNvbS9mbGV4L3YyL3Rva2VucyIsImp3ayI6eyJrdHkiOiJSU0EiLCJlIjoiQVFBQiIsInVzZSI6ImVuYyIsIm4iOiJzR2JGcHVvNWhDWDF2c0dta3Y4OFVDa1N1d2ZIeDVIekFVQVJVNkd5b2ZNV0JxaGQ0ZXFmX0g0QmFMeGt3YTdMamtIbmNEWDZ2bWk1bGtnYi1yNjZoeXMyNTBNOG14TUZWLUN1aWtmQjBoUVlzOU90UUoybllMLTFubU9XQWNPR004ajcyQXB2QjZQSVZYWlpCRFFuXzJNckp4M2dQMXdMVHIzN2tqLURRazFqaHFubmdwUUIySHdPbVVhN09sbElPdjZzTkloMWN5QTdUeUdXeWpoUlQ1TmZTRUxtQkFFeWR1S0hyR3JFNkN0dTlFdUZvc0ZhRkNEYVE2WW9GdThaV2NoMnR4SnJQTWtIYWQtQjdPMkRGeVFTMFBJLWdVZnZxNTl1alVEa2NfYUgyYVRTNDZuU0ZUMFJNZE1nOGF1OWpaQkZISjlZNW9Bd1IwbGhEcHJWc1EiLCJraWQiOiIwOHh0Z3hBRHROUjYwVklRRzlvb1dlZ0xnQXBSOWFlNiJ9fSwiY3R4IjpbeyJkYXRhIjp7ImNsaWVudExpYnJhcnlJbnRlZ3JpdHkiOiJzaGEyNTYtREJ3blN2S0VpS29FR0VJb1VaWjB4ZDlXSU1EWmk2WjM0UHg2RkoxakQ2TVx1MDAzZCIsImNsaWVudExpYnJhcnkiOiJodHRwczovL3Rlc3RmbGV4LmN5YmVyc291cmNlLmNvbS9taWNyb2Zvcm0vYnVuZGxlL3YyLjEwLjAvZmxleC1taWNyb2Zvcm0ubWluLmpzIiwiYWxsb3dlZENhcmROZXR3b3JrcyI6WyJWSVNBIiwiTUFTVEVSQ0FSRCJdLCJjbGllbnRWZXJzaW9uIjoidjIiLCJ0YXJnZXRPcmlnaW5zIjpbImh0dHA6Ly9sb2NhbGhvc3Q6NTUwMCIsImh0dHA6Ly9sb2NhbGhvc3Q6ODA3MSIsImh0dHA6Ly9sb2NhbGhvc3Q6MzAwMCJdLCJtZk9yaWdpbiI6Imh0dHBzOi8vdGVzdGZsZXguY3liZXJzb3VyY2UuY29tIiwiYWxsb3dlZFBheW1lbnRUeXBlcyI6WyJDQVJEIl19LCJ0eXBlIjoibWYtMi4xLjAifV0sImlzcyI6IkZsZXggQVBJIiwiZXhwIjoxNzgxMDY5MjMyLCJpYXQiOjE3ODEwNjgzMzIsImp0aSI6Ik9OdWgzUkpIT0E1VUpoZ2UifQ.kEcO6eyowVVJ1WvTwfHnWQNGH0xSXCKrrXMy-PY3QdQX-sOJcvcOnmNYgXSWAii7tA47RIBIxj-Vnlf3axTJRgz1cnfdB9mKCUd4YgvbA2oQJEw6ZEeLqrTjCaJ7xQy6mtcSHGX7iiOajBusxhEY8nSUUetQod-J8LYJp5XrgELQx4PYKzIcG4iHX2uoLFtzgDHfuKih0gHAWO6ZB9VD8VK6qVCsiNZ9IV01WyOszSLm2RIy9TsOhLSVzl82tLuIK2hXfx14OGpYkKUcx1fnhRJ3Rp1TvXXl1Uj5okMmJFc064h9KtYW9xpo7BtLPTVLae8k4v9UeEO1CAh7MIBoOQ",
          // );

          setFetchError(t("cybersourceError"));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [t]);

  // Step 2: decode the JWT to extract clientLibrary + clientLibraryIntegrity,
  // then load the SDK script from the URL CyberSource provides.
  // Per CyberSource docs these values must not be hardcoded — unique per transaction.
  useEffect(() => {
    if (!captureContext) return;
    let active = true;

    let clientLibrary: string;
    let clientLibraryIntegrity: string;

    try {
      const payloadBase64 = captureContext.split(".")[1];
      const decoded = JSON.parse(
        atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/")),
      );
      clientLibrary =
        decoded.ctx?.[0]?.data?.clientLibrary ?? decoded.clientLibrary;
      clientLibraryIntegrity =
        decoded.ctx?.[0]?.data?.clientLibraryIntegrity ??
        decoded.clientLibraryIntegrity;
      console.log("DECODED_", decoded);
    } catch {
      if (active) {
        setFetchError(t("cybersourceError"));
        setLoading(false);
      }
      return () => {
        active = false;
      };
    }

    if (!clientLibrary) {
      if (active) {
        setFetchError(t("cybersourceError"));
        setLoading(false);
      }
      return () => {
        active = false;
      };
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${clientLibrary}"]`,
    );

    if (existing) {
      // Script tag already in the DOM (StrictMode remount or second render).
      // If window.Flex is already defined the script has fully executed — proceed.
      // If not, it's still loading — attach a listener to the existing tag.
      if ((window as any).Flex) {
        if (active) setSdkReady(true);
      } else {
        const onload = () => {
          if (active) setSdkReady(true);
        };
        existing.addEventListener("load", onload);
        return () => {
          active = false;
          existing.removeEventListener("load", onload);
        };
      }
      return () => {
        active = false;
      };
    }

    const script = document.createElement("script");
    script.src = clientLibrary;
    script.type = "text/javascript";
    script.async = true;
    if (clientLibraryIntegrity) {
      script.integrity = clientLibraryIntegrity;
      script.crossOrigin = "anonymous";
    }
    script.onload = () => {
      if (active) setSdkReady(true);
    };
    script.onerror = () => {
      if (active) {
        setFetchError(t("cybersourceError"));
        setLoading(false);
      }
    };
    document.head.appendChild(script);

    return () => {
      active = false;
    };
  }, [captureContext, t]);

  // Step 3: SDK is ready — create microform + field objects, then reveal the form
  // so React paints the container divs before Step 4 attaches iframes into them.
  useEffect(() => {
    if (!sdkReady || !captureContext) return;
    let active = true;

    try {
      const flex = new (window as any).Flex(captureContext);
      const mf = flex.microform("card");
      const numberField = mf.createField("number", {
        placeholder: "•••• •••• •••• ••••",
      });
      const cvvField = mf.createField("securityCode", { placeholder: "•••" });

      if (active) {
        setMicroform(mf);
        setFields({ number: numberField, securityCode: cvvField });
        setLoading(false);
      }
    } catch {
      if (active) {
        setFetchError(t("cybersourceError"));
        setLoading(false);
      }
    }

    return () => {
      active = false;
    };
  }, [sdkReady, captureContext, t]);

  // Step 4: container divs are now in the DOM (loading=false triggered a repaint),
  // so it is safe to attach the hosted iframes.
  useEffect(() => {
    if (!fields) return;
    fields.number.load("#cs-card-number");
    fields.securityCode.load("#cs-security-code");
  }, [fields]);

  const handlePay = () => {
    if (!microform || !expiryMonth || !expiryYear) return;
    setFieldError(null);
    setSubmitting(true);

    microform.createToken(
      { expirationMonth: expiryMonth, expirationYear: expiryYear },
      (err: any, token: string) => {
        setSubmitting(false);
        if (err) {
          setFieldError(t("cybersourceTokenError"));
          return;
        }
        onPay(token);
      },
    );
  };

  const isPayDisabled =
    disabled || submitting || !microform || !expiryMonth || !expiryYear;

  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{ py: 3 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2, alignSelf: "center", fontSize: 14 }}>
          {t("cybersourceLoading")}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container direction="column" sx={{ py: 1, gap: 2 }}>
      {/* Card number — hosted iframe from CyberSource */}
      <Grid item>
        <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
          {t("cardNumber")}
        </Typography>
        <div id="cs-card-number" style={hostedFieldStyle} />
      </Grid>

      {/* Expiry month / year (plain inputs) + CVV hosted iframe */}
      <Grid item container spacing={1} alignItems="flex-end">
        <Grid item xs={3}>
          <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
            {t("expiryMonth")}
          </Typography>
          <TextField
            size="small"
            fullWidth
            inputProps={{
              maxLength: 2,
              style: {
                height: 23,
                padding: "8.5px 14px",
              },
            }}
            placeholder="MM"
            value={expiryMonth}
            onChange={(e) => setExpiryMonth(e.target.value.replace(/\D/g, ""))}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
            {t("expiryYear")}
          </Typography>
          <TextField
            // label={t("expiryYear")}
            // label="YYYY"
            size="small"
            fullWidth
            inputProps={{
              maxLength: 4,
              style: {
                height: 23,
                padding: "8.5px 14px",
              },
            }}
            placeholder="YYYY"
            value={expiryYear}
            onChange={(e) => setExpiryYear(e.target.value.replace(/\D/g, ""))}
          />
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={3}>
          {/* <Typography variant="caption">{t("securityCode")}</Typography> */}
          <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
            (CVV)
          </Typography>
          <div id="cs-security-code" style={hostedFieldStyle} />
        </Grid>
      </Grid>

      {fieldError && (
        <Grid item>
          <Typography color="error" sx={{ fontSize: 13 }}>
            {fieldError}
          </Typography>
        </Grid>
      )}

      {fetchError && (
        <Typography color="error" sx={{ py: 1, fontSize: 14 }}>
          {fetchError}
        </Typography>
      )}

      <Grid item>
        <ButtonStyled
          disabled={isPayDisabled}
          onClick={handlePay}
          label={
            submitting
              ? "..."
              :  t("paymentButton")
          }
          style={{
            width: "100%",
            height: 38,
            backgroundColor: isPayDisabled
              ? theme?.primaryColor
              : theme?.secondaryColor || palette.primary.main,
            color:
              getContrastTextColor(theme?.secondaryColor) === "light"
                ? palette.text.light
                : palette.black,
          }}
        />
      </Grid>
    </Grid>
  );
};

export default CyberSourceMicroform;
